import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, Phone, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, waLink } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { AdminHeading } from "@/pages/admin/AdminShell";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const PAY = ["unpaid", "pending", "paid", "failed", "expired", "refunded"];
const sel = "border border-[#241914]/20 bg-white/70 px-2 py-1.5 text-xs outline-none";

function OrderRow({ o, onChange }) {
  const { t, tr, fmt } = useLang();
  const [open, setOpen] = useState(false);
  const patch = async (body) => {
    try {
      const { data } = await api.patch(`/admin/orders/${o.id}`, body);
      onChange(data);
      toast.success(t("a_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const date = o.created_at ? new Date(o.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }) : "";
  return (
    <div className="border-b border-[#241914]/10" data-testid={`admin-order-row-${o.order_number}`}>
      <div className="grid grid-cols-2 items-center gap-3 py-4 text-sm md:grid-cols-[1fr_1.3fr_0.8fr_1fr_1fr_auto]">
        <div><p className="font-latin text-[#8a6d35]">{o.order_number}</p><p className="text-xs text-[#241914]/60">{date}</p></div>
        <div className="min-w-0"><p className="truncate">{o.customer?.full_name}</p><p className="text-xs text-[#241914]/60">{o.city_name}</p></div>
        <div><p>{fmt(o.total)}</p><p className="text-xs text-[#241914]/60">{t(o.payment_method === "cod" ? "svc_cod_t" : o.payment_method === "card" ? "svc_card_t" : "svc_wa_t")}</p></div>
        <select data-testid={`admin-order-status-${o.order_number}`} className={sel} value={o.status} onChange={(e) => patch({ status: e.target.value })}>
          {STATUSES.map((s) => <option key={s} value={s}>{t(`os_${s}`)}</option>)}
        </select>
        <select data-testid={`admin-order-payment-${o.order_number}`} className={sel} value={o.payment_status} onChange={(e) => patch({ payment_status: e.target.value })}>
          {PAY.map((s) => <option key={s} value={s}>{t(`ps_${s}`)}</option>)}
        </select>
        <button data-testid={`admin-order-expand-${o.order_number}`} onClick={() => setOpen(!open)} className="justify-self-end p-2"><ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} /></button>
      </div>
      {open && (
        <div className="mb-4 grid grid-cols-1 gap-6 bg-[#E8DFD0]/50 p-5 text-sm md:grid-cols-2" data-testid={`admin-order-details-${o.order_number}`}>
          <div>
            <p className="eyebrow !text-[#8a6d35]">{t("a_customer")}</p>
            <p className="mt-2">{o.customer?.full_name}</p>
            <p dir="ltr" className="text-start">{o.customer?.phone}</p>
            {o.customer?.email && <p dir="ltr" className="text-start">{o.customer.email}</p>}
            <p className="mt-2">{o.customer?.address} — {o.city_name}</p>
            {o.customer?.notes && <p className="mt-2 italic text-[#241914]/75">“{o.customer.notes}”</p>}
            <div className="mt-3 flex gap-4">
              <a href={`tel:${o.customer?.phone}`} className="inline-flex items-center gap-1 underline"><Phone className="h-3.5 w-3.5" /> {t("a_call")}</a>
              <a href={waLink(o.customer?.phone?.replace(/^0/, "212"), `${o.order_number}`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 underline"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
            </div>
          </div>
          <div>
            <p className="eyebrow !text-[#8a6d35]">{t("a_items")}</p>
            {o.items.map((i) => (
              <div key={i.product_id} className="mt-2 flex justify-between"><span>{tr(i.name)} {i.volume} × {i.qty}</span><span>{fmt(i.price * i.qty)}</span></div>
            ))}
            <div className="mt-3 border-t border-[#241914]/15 pt-2">
              <div className="flex justify-between"><span>{t("delivery")}</span><span>{fmt(o.delivery_fee)}</span></div>
              <div className="flex justify-between font-medium"><span>{t("total")}</span><span>{fmt(o.total)}</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminOrders() {
  const { t } = useLang();
  const [list, setList] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    api.get("/admin/orders").then((r) => setList(Array.isArray(r.data) ? r.data : [])).catch(() => setList([]));
  }, []);
  useEffect(() => {
    load();
  }, [load]);

  const shown = useMemo(() => {
    const s = q.trim().toLowerCase();
    return list.filter((o) => (filter === "all" || o.status === filter) && (!s || [o.order_number, o.customer?.full_name, o.customer?.phone].some((x) => (x || "").toLowerCase().includes(s))));
  }, [list, filter, q]);

  return (
    <div data-testid="admin-orders">
      <AdminHeading title={t("a_orders")} />
      <div className="mb-6 flex flex-wrap items-center gap-2">
        {["all", ...STATUSES].map((s) => (
          <button key={s} data-testid={`admin-orders-filter-${s}`} onClick={() => setFilter(s)} className={`px-3 py-1.5 text-xs ${filter === s ? "bg-[#0B0B0C] text-[#F7F3EB]" : "border border-[#241914]/20"}`}>
            {s === "all" ? t("a_all") : t(`os_${s}`)}
          </button>
        ))}
        <input data-testid="admin-orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("a_search")} className="ms-auto w-full border border-[#241914]/20 bg-white/60 px-3 py-2 text-sm outline-none sm:w-72" />
      </div>
      {shown.length === 0 ? (
        <p className="py-16 text-center text-[#241914]/70" data-testid="admin-orders-empty">{t("a_no_orders")}</p>
      ) : (
        <div className="border-t border-[#241914]/10">
          {shown.map((o) => (
            <OrderRow key={o.id} o={o} onChange={(d) => setList((l) => l.map((x) => (x.id === d.id ? d : x)))} />
          ))}
        </div>
      )}
    </div>
  );
}
